import React, { useMemo } from 'react';
import { sanitizeHtml } from '../../utils/textProcessing.js';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';
import SocialIcon from '../ui/SocialIcon.jsx';
import { SOCIAL_CHANNELS } from '../../constants/channels.js';

const FB_COLOR =
  (SOCIAL_CHANNELS.find((c) => c.id === 'facebook') || {}).color || '#1877F2';

const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

const splitParagraphs = (text) =>
  String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

const renderTag = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  return s.startsWith('#') ? s : `#${s}`;
};

const SectionLabel = ({ children, color, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: color || t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

const FacebookResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  const color = FB_COLOR;
  const eId = entry.id || 'facebook';

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;

  const hook = (c.hook || '').trim();
  const body = (c.body || '').trim();
  const cta = (c.cta || '').trim();
  const hashtags = Array.isArray(c.hashtags) ? c.hashtags.filter(Boolean) : [];

  const bodyParas = useMemo(() => splitParagraphs(body), [body]);

  return (
    <div>
      {/* Region 1 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 2 — Facebook platform pill */}
      <div style={{ marginBottom: '14px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '20px',
            background: `${color}10`,
            border: `1px solid ${color}30`,
            color,
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          <SocialIcon id="facebook" size={14} color={color} />
          Facebook
        </span>
      </div>

      {/* Region 3 — Hook (bold paragraph, no label, no box) */}
      {hook && (
        <p
          style={{
            margin: '0 0 12px',
            fontSize: '15px',
            fontWeight: 700,
            lineHeight: 1.55,
            color: t.text,
            whiteSpace: 'pre-wrap',
          }}
        >
          {hook}
        </p>
      )}

      {/* Region 4 — Body (plain prose, no label, no box) */}
      {bodyParas.length > 0 && (
        <div style={{ marginBottom: '14px', color: t.text }}>
          {bodyParas.map((p, i) => (
            <p
              key={i}
              style={{
                margin: i === bodyParas.length - 1 ? 0 : '0 0 10px',
                fontSize: '14px',
                lineHeight: 1.7,
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
            />
          ))}
        </div>
      )}

      {/* Region 5 — CTA (blue italic text, no label, no box) */}
      {cta && (
        <p
          style={{
            margin: '0 0 14px',
            fontSize: '14px',
            lineHeight: 1.6,
            color,
            fontStyle: 'italic',
            whiteSpace: 'pre-wrap',
          }}
        >
          {cta}
        </p>
      )}

      {/* Region 6 — Hashtag chips (no label, neutral chips) */}
      {hashtags.length > 0 && (
        <div style={{ marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {hashtags.map((tag, i) => (
            <span
              key={i}
              style={{
                padding: '3px 10px',
                borderRadius: '6px',
                background: w(0.04),
                border: `1px solid ${w(0.06)}`,
                color: t.textMuted || '#7a7a8e',
                fontSize: '12px',
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {renderTag(tag)}
            </span>
          ))}
        </div>
      )}

      {/* Region 7 — Sources cited */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />
    </div>
  );
};

export default FacebookResultEntry;
